import {
  ActionCard,
  Assessment,
  Question,
  Element,
  ElementKind,
  Section,
  Dimension,
  ScoreMode,
  AssessmentType,
  QuestionTypes,
} from '@playbooks/core/src/interfaces/ActionCard'

import {
  getCoefficient,
  getDimensionsMap,
  getMultiAssessmentMaxRank,
  getMultiAssessmentRank,
  getRelevantAssessments,
  getSingleAssessmentRank,
  handleAssessmentForDimensions,
  handleQuestionsForDimensions,
  isAssessmentRelevant,
  isDataVisible,
  isQuestionRelevant,
  isSectionRelevant,
} from './utility'

import { sectionTypes } from '@playbooks/core/src/constants'
import { MAX_SCORE_LIMIT } from './constants'
import isConditionSatisfied from './isConditionSatisfied'
import isSignatureQuestion from './isSignatureQuestion'
import { DimensionsData, DropdownItem, SignatureData } from './types'

/**
 * !!! Important !!! This function mutates the state parameter
 * Calculates scores for the action card
 *
 * @param {ActionCard} state - The current state of the action card
 * @returns {ActionCard} The updated state of the action card with calculated scores.
 */
export default (state: ActionCard) => {
  let assessmentsActiveSuperScoreCollection: {
    sepId: number
    score: number
    responseId: number
  }[] = []

  return calculate(state)

  function getCalculationRelevantData(elements: Element[], conditionalItems: number[]) {
    const data = elements.reduce<{
      assessments: Assessment[]
      questions: Question[]
    }>(
      (acc, current) => {
        if (current.kind === ElementKind.ASSESSMENT) {
          if (isAssessmentRelevant(current as Assessment, conditionalItems, true)) {
            acc.assessments.push(current as Assessment)
          }
        }

        if (current.kind === ElementKind.QUESTION) {
          if (isQuestionRelevant(current as Question, conditionalItems)) {
            acc.questions.push(current as Question)
          }
        }

        return acc
      },
      { assessments: [], questions: [] },
    )

    return data
  }

  function getDimensionRelevantElements(sections: Section[], conditionalItems: number[]) {
    return sections.flatMap(sec => {
      if (!isDataVisible(sec, conditionalItems)) return []

      const { assessments: assessmentsFromContentSec, questions: questionsFromContentSec } = getCalculationRelevantData(
        sec.elements || [],
        conditionalItems,
      )

      const allAssessments = [...assessmentsFromContentSec].map(handleAssessmentForDimensions)
      const allQuestions = [...questionsFromContentSec].map(handleQuestionsForDimensions)

      return [...allAssessments, ...allQuestions]
    })
  }

  function getDimensionsMaxValue(scoringDimensions: Dimension[], dimensionRelevantElements: (Question | Assessment)[]) {
    return scoringDimensions?.reduce(
      (acc, cur) => {
        const allDimRelated = dimensionRelevantElements.filter(item => item.scoringDimensionId === cur.id)

        const dimMax = allDimRelated.reduce((dAcc, dCur) => {
          if (dCur.kind === ElementKind.ASSESSMENT) {
            const assessment = dCur as Assessment
            const max = getMultiAssessmentMaxRank(assessment)

            return dAcc + max
          }

          if (dCur.kind === ElementKind.QUESTION) {
            return dAcc + (dCur as Question).rank
          }

          return dAcc
        }, 0)

        acc.maxValueByDimensions = acc.maxValueByDimensions + dimMax * cur.coefficient
        acc.dimensionsMaxValues[cur.id] = dimMax

        return acc
      },
      { maxValueByDimensions: 0, dimensionsMaxValues: {} as { [x: number]: number } },
    )
  }

  function calcDimensionScore(
    scoringDimensions: Dimension[],
    dimensionRelevantElements: (Question | Assessment)[],
    dimensionsData: DimensionsData,
  ) {
    const { maxValueByDimensions, dimensionsMaxValues } = getDimensionsMaxValue(
      scoringDimensions,
      dimensionRelevantElements,
    )

    const dimScoreData = dimensionRelevantElements.reduce(
      (acc, curEll) => {
        const dimId = curEll.scoringDimensionId

        if (!dimId) {
          return acc
        }

        const coefficient = getCoefficient(dimensionsData.data, dimId)
        const dimScore = curEll.dimensionRelatedScore || 0
        const certainDimMaxValue = dimensionsMaxValues[dimId]!

        acc.relevantElementScore = acc.relevantElementScore + dimScore * coefficient

        if (curEll.dimensionRelatedScore) {
          const prevActualScoreValue = acc.actualScore[dimId] || 0

          acc.actualScore[dimId] =
            (curEll.dimensionRelatedScore / certainDimMaxValue) * MAX_SCORE_LIMIT + prevActualScoreValue
        }

        return acc
      },
      { relevantElementScore: 0, actualScore: {} as { [x: number]: number } },
    )

    const dimScore = (dimScoreData.relevantElementScore / maxValueByDimensions) * MAX_SCORE_LIMIT || 0

    return { dimScore, maxValueByDimensions, actualScore: dimScoreData.actualScore }
  }

  function getDimensionScore(data: {
    sections: Section[]
    conditionalItems: number[]
    scoringDimensions: Dimension[]
    scoreMode: ScoreMode
    enabledSeparators: number[]
  }) {
    const { sections, conditionalItems, scoringDimensions, scoreMode, enabledSeparators } = data
    const sectionsForDimensions = getSectionsForCalculation(sections, scoreMode, enabledSeparators, conditionalItems)
    const dimensionRelevantElements = getDimensionRelevantElements(sectionsForDimensions, conditionalItems)
    const dimensionsData = getDimensionsMap(scoringDimensions)

    const {
      dimScore: dimensionScore,
      maxValueByDimensions,
      actualScore,
    } = calcDimensionScore(scoringDimensions || [], dimensionRelevantElements, dimensionsData)

    return { dimensionScore, dimensionsData, dimensionRelevantElements, maxValueByDimensions, actualScore }
  }

  function resetDimensionsScores(state: ActionCard) {
    state.scoringDimensions = state.scoringDimensions?.map(dimension => {
      dimension.score = 0

      return dimension
    })
  }

  function calculate(state: ActionCard) {
    calcContentSectionScore(state)
    calculateSeparatorScore(state)

    if (state.useScoringDimensions) {
      resetDimensionsScores(state)

      const { dimensionRelevantElements, dimensionScore, dimensionsData, maxValueByDimensions, actualScore } =
        getDimensionScore({
          sections: state.sections,
          scoringDimensions: state.scoringDimensions || [],
          conditionalItems: state.conditionalItems || [],
          scoreMode: state.scoreMode || ScoreMode.ENTIRE,
          enabledSeparators: state.enabledSeparators || [],
        })

      state.score = dimensionScore

      dimensionRelevantElements.forEach(el => {
        const dimId = el.scoringDimensionId

        if (!dimId) return

        const dim = dimensionsData.data[dimId]
        const coefficient = getCoefficient(dimensionsData.data, dimId)
        const elementDimScore = el.dimensionRelatedScore || 0

        if ((!coefficient && typeof coefficient !== 'number') || !dim) {
          return
        }

        const prevScore = dim!.score || 0
        dim.score = prevScore + elementDimScore * coefficient || 0

        dim.actual = actualScore[dimId] || 0
      })

      state.scoringDimensions = state.scoringDimensions?.map(dimension => {
        const score = +((dimension.score! / maxValueByDimensions) * MAX_SCORE_LIMIT) || 0
        if (!score && dimensionsData.data[dimension.id]?.actual) {
          dimensionsData.data[dimension.id]!.actual = 0
        }
        return {
          ...dimension,
          score,
        }
      })

      return state
    }
    const sections = getSectionsForCalculation(
      state.sections,
      state.scoreMode || ScoreMode.ENTIRE,
      state.enabledSeparators || [],
      state.conditionalItems || [],
    )
    state.score = calculateTotalScore(state, sections)

    return state
  }

  function calculateAssessmentScore(assessment: Assessment, separatorId: number): number {
    const superScoreResponse = assessment.responses.filter(resp => resp.superScore)

    superScoreResponse.forEach(superReps => {
      if (superReps.checked) {
        assessmentsActiveSuperScoreCollection.push({
          responseId: superReps.id,
          sepId: separatorId,
          score: superReps.rank,
        })
      } else {
        const activeSuperScoreItems = assessmentsActiveSuperScoreCollection.filter(item => {
          return item.responseId !== superReps.id
        })

        assessmentsActiveSuperScoreCollection = activeSuperScoreItems
      }
    })

    if (assessment.type == AssessmentType.MULTI) {
      const rank = getMultiAssessmentRank(assessment)

      assessment.dimensionRelatedScore = rank
      return rank
    } else {
      return getSingleAssessmentRank(assessment)
    }
  }

  function calculateTotalScore(state: ActionCard, sections: Section[]): number {
    const relevantSections = sections.filter(section => section.type !== sectionTypes.SEPARATOR)

    const allVisibleSections = relevantSections.filter(section => isDataVisible(section, state.conditionalItems || []))

    if (allVisibleSections.length === 0) {
      return 0
    }

    const totalScore = allVisibleSections.reduce((acc, section) => {
      return acc + (section.score || 0)
    }, 0)

    return totalScore / allVisibleSections.length
  }

  function getSectionsForCalculation(
    sections: Section[],
    scoreMode: ScoreMode,
    enabledSeparators: number[],
    conditionalItems: number[],
  ) {
    const allAvailableSections = sections.filter(sec => {
      return scoreMode === ScoreMode.ENTIRE
        ? true
        : sec.idOfSep === -1 ||
            enabledSeparators.some(sepId => {
              return sec.idOfSep === sepId
            })
    })

    const forCalc = allAvailableSections.filter(sec => {
      if (sec.type !== sectionTypes.CONTENT) return true

      const elements = sec.elements || []

      const hasRelevantElements = elements.some(el => {
        if (el.kind === ElementKind.ASSESSMENT) {
          return isDataVisible(el as Assessment, conditionalItems)
        }

        if (el.kind === ElementKind.QUESTION) {
          return isDataVisible(el as Question, conditionalItems)
        }

        return false
      })

      return hasRelevantElements
    })

    return forCalc
  }

  function calculateSeparatorScore(state: ActionCard) {
    const mapOfSeparatorsSectionsScores: { [x: number]: number[] } = {}

    if (state.useScoringDimensions) {
      const sectionsBySepId = state.sections.reduce<Record<number, Section[]>>((acc, curr, index) => {
        if (index === 0 && curr.type !== sectionTypes.SEPARATOR) {
          acc[-1] = []
        }

        if (curr.type === sectionTypes.SEPARATOR) {
          acc[curr.id] = []
        }

        if (curr.idOfSep) {
          acc[curr.idOfSep]?.push(curr)
        }

        return acc
      }, {})

      state.sections.forEach(sep => {
        if (sep.type !== sectionTypes.SEPARATOR) return

        const { dimensionScore } = getDimensionScore({
          sections: sectionsBySepId[sep.id] || [],
          conditionalItems: state.conditionalItems || [],
          enabledSeparators: state.enabledSeparators || [],
          scoreMode: state.scoreMode || ScoreMode.ENTIRE,
          scoringDimensions: state.scoringDimensions || [],
        })

        sep.score = +dimensionScore.toFixed(2)
      })

      return
    }

    state.sections.forEach(sec => {
      if (!isSectionRelevant(sec, state.conditionalItems || []) || !sec.idOfSep) return

      if (!isConditionSatisfied(sec.tags || null, state.conditionalItems || [], sec.conditionRule || null)) return

      let sectionScore = !sec.tags?.length
        ? sec.score || 0
        : isConditionSatisfied(sec.tags || null, state.conditionalItems || [], sec.conditionRule || null)
          ? sec.score || 0
          : 0

      mapOfSeparatorsSectionsScores[sec.idOfSep] = [...(mapOfSeparatorsSectionsScores[sec.idOfSep] || []), sectionScore]
    })

    state.sections.forEach(sep => {
      if (sep.type !== sectionTypes.SEPARATOR) return

      const secScores = mapOfSeparatorsSectionsScores[sep.id]

      if (typeof secScores === 'undefined') {
        sep.score = 0

        return
      }

      const allSectionInSepScore = secScores.reduce((acc, current) => {
        return acc + current
      }, 0)

      const assessmentScore = assessmentsActiveSuperScoreCollection.find(item => item.sepId === sep.id)

      if (assessmentScore) {
        sep.score = assessmentScore.score

        return
      }

      sep.score = allSectionInSepScore / secScores.length
    })
  }

  function getDropdownTopRank(data: DropdownItem[]): number {
    return [...data].sort((a, b) => (a.rank < b.rank ? 1 : -1))[0]?.rank || 0
  }

  function resolveQuestionRankData(question: Question) {
    const value = typeof question.value === 'number' ? String(question.value) : question.value
    const data = (question.data as DropdownItem[]) || []

    if (question.type === QuestionTypes.DROPDOWN) {
      if (value && data.length && data.some(item => !!item.rank)) {
        const selectedAnswer = data.find(item => item.id.toString() === (value as string))
        const selectedRank = selectedAnswer?.rank ?? question.rank
        const maxRank = Math.max(question.rank, getDropdownTopRank(data))

        return selectedRank
          ? { rank: selectedRank, maxRank, value }
          : { rank: maxRank, maxRank: undefined, value: undefined }
      }

      if (data.length) {
        const rank = Math.max(question.rank, getDropdownTopRank(data))
        return rank ? { rank, maxRank: undefined, value } : null
      }
    }

    return question.rank ? { rank: question.rank, maxRank: undefined, value } : null
  }

  function getQuestionsScoringData(questions: Question[], conditionalItems: number[]) {
    return questions.reduce(
      (acc, question) => {
        if (!isQuestionRelevant(question, conditionalItems)) return acc

        const resolved = resolveQuestionRankData(question)
        if (!resolved) return acc

        const { rank, maxRank, value } = resolved
        let score

        // In case of file question
        if (Array.isArray(question.value) && question.value.length === 0) {
          score = 0
        } else if (isSignatureQuestion(question)) {
          const signatureData = value as SignatureData

          score = signatureData?.points.length ? rank : 0
        } else {
          score = value ? rank : 0
        }

        acc.score += score
        acc.rank += maxRank || rank
        acc.questionScores.push(score)

        return acc
      },
      { score: 0, rank: 0, questionScores: [] as number[] },
    )
  }

  // Helper function to calculate dimension score for a section
  function calculateSectionDimensionScore(section: Section, state: ActionCard): number {
    const { dimensionScore } = getDimensionScore({
      sections: [section],
      conditionalItems: state.conditionalItems || [],
      scoringDimensions: state.scoringDimensions || [],
      scoreMode: state.scoreMode || ScoreMode.ENTIRE,
      enabledSeparators: state.enabledSeparators || [],
    })
    return dimensionScore
  }

  function calcContentSectionScore(state: ActionCard) {
    state.sections.forEach(sec => {
      if (sec.type !== sectionTypes.CONTENT || !sec.elements?.length) return

      if (state.useScoringDimensions) {
        sec.score = calculateSectionDimensionScore(sec, state)
        return
      }

      const { assessments, questions } = getCalculationRelevantData(sec.elements, state.conditionalItems || [])
      const relevantAssessments = getRelevantAssessments(assessments, state.conditionalItems || [])
      const { questionScores } = getQuestionsScoringData(questions, state.conditionalItems || [])

      const assessmentScores = relevantAssessments.map(assess => {
        if (!sec.idOfSep) return 0
        return calculateAssessmentScore(assess, sec.idOfSep)
      })

      const allScoredElements = [...questionScores, ...assessmentScores]
      sec.score = allScoredElements.reduce((acc, curr) => acc + curr, 0) / allScoredElements.length || 0
    })
  }
}
